import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTableCard from '@/components/DataTableCard';
import EmptyState from '@/components/EmptyState';
import OrgFormDialog from '@/components/entities/OrgFormDialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2 } from 'lucide-react';

const Organizations = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [people, setPeople] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [orgsRes, peopleRes] = await Promise.all([
        api.get('/organizations'),
        api.get('/persons').catch(() => ({ data: [] })),
      ]);
      setOrgs(orgsRes.data);
      setPeople(peopleRes.data);
    } catch (err) {
      toast.error('Failed to load organizations');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        toast.error('Access denied. Staff privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const openCreate = () => { setEditing(null); setShowDialog(true); };
  const openEdit = (o) => { setEditing(o); setShowDialog(true); };

  const deleteOrg = async (o) => {
    if (!window.confirm(`Delete "${o.name}"?`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success('Organization deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const personName = (id) => people.find((p) => p.id === id)?.name || '—';

  const visible = orgs.filter(
    (o) => !search || o.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Organizations"
        icon={Building2}
        titleTestId="organizations-title"
        actions={
          <Button onClick={openCreate} data-testid="new-org-btn">
            <Plus size={20} /> New Organization
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Input
          placeholder="Search organizations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:w-80"
          data-testid="org-search"
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Building2} message="No organizations found" />
      ) : (
        <DataTableCard>
          <table className="w-full text-sm" data-testid="organizations-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-3 font-medium" scope="col">Name</th>
                <th className="p-3 font-medium" scope="col">Primary contact</th>
                <th className="p-3 font-medium" scope="col">Contacts</th>
                <th className="p-3 font-medium text-right" scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr key={o.id} className="border-t" data-testid={`org-row-${o.id}`}>
                  <td className="p-3 font-medium">{o.name}</td>
                  <td className="p-3">{personName(o.primary_contact_id)}</td>
                  <td className="p-3">{(o.contacts || []).length}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openEdit(o)} data-testid={`edit-org-${o.id}`}>
                        <Edit size={16} /> Edit
                      </Button>
                      {user.role !== 'associate' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteOrg(o)}
                          data-testid={`delete-org-${o.id}`}
                          aria-label={`Delete organization ${o.name}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableCard>
      )}

      <OrgFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        organization={editing}
        onSaved={fetchData}
      />
    </PageShell>
  );
};

export default Organizations;
